import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';

import { useTerminal } from 'lib/hooks/queries/terminals/useTerminal';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import useModal from 'lib/hooks/useModal';

import { currentPractice } from 'components/practice/practiceState';

import {
  genericMethodsApi,
  paymentMethodApiElavon,
  paymentMethodApiPayroc
} from '../utils/paymentMethodApi';

export const PaymentContext = createContext();
export const PaymentContextApi = createContext();

export const getMutationFn = (provider, method) => {
  if (!method) return null;

  const providerMethodsApiMap = {
    elavon: paymentMethodApiElavon,
    payroc: paymentMethodApiPayroc
  };

  const currentMethod =
    providerMethodsApiMap[provider]?.[method] ?? genericMethodsApi[method] ?? null;

  return currentMethod;
};

export const PaymentContextProvider = ({
  children,
  amount,
  initialMethod,
  invoiceToken,
  patientId,
  appointmentId,
  invoices = [],
  practiceId: practiceIdProp,
  onSuccess = () => {},
  onError = () => {},
  checkin = false
}) => {
  const navigate = useNavigate();
  const practice = useRecoilValue(currentPractice);
  const practiceId = practiceIdProp || practice.id;
  const [postedResponse, setPostedResponse] = useState(null);
  const formikValuesRef = useRef(null);
  const processPaymentRef = useRef(null);

  const { isOpen, openModal, closeModal } = useModal(false);

  const formik = useFormik({
    initialValues: {
      amount,
      appliedBalance: 0,
      method: initialMethod,
      methodDetail: null,
      invoices,
      appointmentId,
      surchargeAmount: 0,
      terminalId: null,
      processingTerminalId: null,
      patientId,
      payerId: null,
      invoiceToken,
      saveCard: false,
      paymentType: null,
      paymentOption: null,
      surchargeEnabled: practice?.surcharge_enabled
    },
    enableReinitialize: true
  });

  formikValuesRef.current = formik.values;

  const { data, isLoading: isTerminalLoading } = useTerminal({
    params: { practiceId, patientId, attribute: 'online' },
    dependencies: [practiceId, patientId]
  });

  const terminal = data?.terminal;
  const provider = terminal?.provider;
  const isFullAppliedBalance = formik.values.appliedBalance > 0 && formik.values.amount <= 0;
  const appliedMethod = isFullAppliedBalance ? 'applied-balance' : formik.values.method;

  processPaymentRef.current = getMutationFn(provider, appliedMethod);

  const { mutateAsync, isPending: isCharging } = useCustomMutation({
    mutationFn: (params) => processPaymentRef.current(navigate, params),
    disableAlert: true,
    invalidateQueryKey: 'patientBalance',
    onSuccess,
    handleResponse: onError
  });

  const handlePayment = useCallback(
    async (data) => {
      try {
        if (!processPaymentRef.current) return;

        const finalValues = Object.fromEntries(
          Object.entries(formikValuesRef.current).filter(([_, v]) => v !== undefined && v !== null)
        );

        const response = await mutateAsync({
          token: data?.token,
          ...finalValues,
          ...data,
          practiceId,
          checkin
        });

        setPostedResponse(response);
        return response;
      } catch (error) {
        console.error(error);
        onError(true);
      }
    },
    [provider, formik.values, mutateAsync, practiceId, checkin, onError]
  );

  const togglePaymentMethod = useCallback(
    (key) => {
      const isSameMethod = formik.values.method === key;
      formik.setFieldValue('methodDetail', null);
      formik.setFieldValue('method', isSameMethod ? null : key);
      closeModal();
    },
    [formik.values.method, formik.setFieldValue, closeModal]
  );

  const isLoading = isTerminalLoading || isCharging;
  const isDisabled =
    !formik.values.method || isLoading || !processPaymentRef.current || formik.values.amount <= 0;

  const disabledTooltip =
    (formik.values.amount <= 0 && 'Amount must be greater than 0') ||
    (!formik.values.method && 'Please select a payment method') ||
    (!processPaymentRef.current && 'Payment method not supported') ||
    (isLoading && 'Processing payment...') ||
    '';

  const contextValue = useMemo(
    () => ({
      values: formik.values,
      isLoading,
      isDisabled,
      disabledTooltip,
      provider,
      terminalId: terminal?.terminalId,
      postedResponse,
      isOpen,
      isTerminalLoading,
      isCharging,
      terminal
    }),
    [
      formik.values,
      isLoading,
      isDisabled,
      disabledTooltip,
      provider,
      terminal?.terminalId,
      postedResponse,
      isOpen,
      isTerminalLoading,
      isCharging,
      terminal
    ]
  );

  const contextApi = useMemo(
    () => ({
      togglePaymentMethod,
      setFieldValue: formik.setFieldValue,
      handlePayment,
      openModal,
      closeModal
    }),
    [togglePaymentMethod, handlePayment, openModal, closeModal]
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      <PaymentContextApi.Provider value={contextApi}>{children}</PaymentContextApi.Provider>
    </PaymentContext.Provider>
  );
};

export const usePaymentContext = () => useContext(PaymentContext);
export const usePaymentContextApi = () => useContext(PaymentContextApi);
